import { Injectable } from '@angular/core';

import { AttributeDataService } from '../../services/attribute-data.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { computeLayoutHugBox, Point } from '../utils/compute-fit-box';
import {
  LayoutMetadata,
  LayoutMetadataService,
} from './layout-metadata.service';

// Applies switch-time box sizing: when a user picks a different layout for
// an already-added rise-layout-* component, its box is recomputed to hug
// the newly-chosen layout's aspect ratio — unless the user has manually
// resized the component (customization.userSized), in which case their
// size is respected and nothing here touches it.
@Injectable({
  providedIn: 'root',
})
export class LayoutHugBoxService {
  // Layout-*.component.ts instances are singletons reused across whichever
  // component of that type is currently selected, so a switch triggered
  // for one component can still be in flight when the user switches again
  // before the first metadata fetch resolves. A per-componentId generation
  // counter, incremented on every call, lets a stale, superseded fetch
  // recognize it should no longer write. Tracking the layoutId instead
  // isn't enough: in an A -> B -> A click sequence, both A calls share the
  // same layoutId, so the first A's late-arriving response would still
  // match a layoutId-only guard even though a newer A call has since
  // superseded it — the generation counter distinguishes the two calls
  // even when they request the same layout.
  private readonly latestRequestGeneration = new Map<string, number>();

  constructor(
    private layoutMetadataService: LayoutMetadataService,
    private attributeDataService: AttributeDataService,
    private broadcaster: BroadcasterService,
  ) {}

  // `repeatCount` only matters for a layout whose metadata declares
  // `repeating` (e.g. Countdown's visible block count, Weather's
  // daysToShow) — pass the component's own current live value; it's
  // ignored otherwise. A caller that can only work out the count from the
  // layout's metadata (add-time sizing reads the repeating.countProperty
  // from it) passes a function instead, which is called with that metadata
  // once it has loaded. That lets the caller call this method immediately,
  // reserving its place in the generation order before any await, rather
  // than awaiting the metadata itself first and risking a later layout
  // switch being superseded by this earlier request.
  //
  // Never throws: a metadata lookup failure or a missing/invalid layoutId
  // simply leaves the current box untouched.
  async applyHugBoxForLayoutSwitch(
    componentId: string,
    layoutId: string,
    repeatCount:
      | number
      | ((metadata: LayoutMetadata | null | undefined) => number) = 1,
  ): Promise<void> {
    if (!componentId || !layoutId) {
      return;
    }

    const requestGeneration =
      (this.latestRequestGeneration.get(componentId) ?? 0) + 1;
    this.latestRequestGeneration.set(componentId, requestGeneration);

    // Remembered now, before the await, so that a component deleted while
    // the metadata loads can be told apart from one that was never a user
    // component. Only user-added components can be deleted. Base components
    // (part of the template, never deletable) aren't in getUserComponents()
    // but must still be sized: a presentation generated by AI creates its
    // layout components as base components at the root of <body>, where the
    // editor lets the user move and resize them like any other.
    const wasUserComponent = this._isUserComponent(componentId);

    if (this._isUserSized(componentId)) {
      return;
    }

    const metadataMap = await this.layoutMetadataService.getMetadata([
      layoutId,
    ]);

    // A newer switch (for this same component) was requested while this
    // fetch was in flight — let that one apply its own box instead. Compares
    // generations, not layoutId, so a same-layout re-click (A -> B -> A)
    // still supersedes an earlier in-flight call for that same A.
    if (this.latestRequestGeneration.get(componentId) !== requestGeneration) {
      return;
    }

    // The user may have manually resized while the fetch was in flight.
    if (this._isUserSized(componentId)) {
      return;
    }

    // The component may have been deleted while the fetch was in flight.
    // Writing now would make AttributeDataService recreate a bare entry for
    // it, which would end up in the saved presentation as orphan data.
    if (wasUserComponent && !this._isUserComponent(componentId)) {
      return;
    }

    const metadata = metadataMap[layoutId];
    const resolvedRepeatCount =
      typeof repeatCount === 'function' ? repeatCount(metadata) : repeatCount;
    const { width: canvasWidth, height: canvasHeight } =
      this.attributeDataService.getCanvasDimensions();

    const existingCustomization =
      this.attributeDataService.getAttributeData(
        componentId,
        'customization',
      ) || {};

    // Anchor the new box on wherever the component already sits, rather
    // than resetting it to canvas-center on every switch — a user who
    // dragged the component (a move, not a resize — see
    // TemplateAttributeEditorComponent's userSized tracking) shouldn't see
    // it jump back to the middle of the canvas just because they picked a
    // different layout. Falls back to canvas-center (see computeFitBox)
    // when there's no existing box yet, e.g. add-time metadata wasn't
    // available so no customization was ever set.
    const anchorCenter = this._centerOf(existingCustomization);

    const box = computeLayoutHugBox(
      metadata,
      resolvedRepeatCount,
      canvasWidth,
      canvasHeight,
      anchorCenter,
    );

    if (!box) {
      return;
    }

    this.attributeDataService.setAttributeData(componentId, 'customization', {
      ...existingCustomization,
      ...box,
    });

    // The component is selected while its layout is switched (that's how
    // the switch is triggered), and the selection overlay only ever
    // re-measures on an explicit highlight — it won't pick up this async
    // box change on its own. componentBoxUpdated pushes immediately and
    // re-highlights the current selection so the overlay reflects the new
    // geometry right away. See PresentationPreviewComponent's handling of
    // this same event for the image-hugging feature.
    this.broadcaster.emit('risevision.template-editor.componentBoxUpdated');
  }

  private _isUserComponent(componentId: string): boolean {
    return this.attributeDataService
      .getUserComponents()
      .some((component) => component.id === componentId);
  }

  private _isUserSized(componentId: string): boolean {
    return !!this.attributeDataService.getCustomization(
      componentId,
      'userSized',
    );
  }

  private _centerOf(customization: Record<string, unknown>): Point | undefined {
    const left = parseFloat(customization?.['left'] as string);
    const top = parseFloat(customization?.['top'] as string);
    const width = parseFloat(customization?.['width'] as string);
    const height = parseFloat(customization?.['height'] as string);

    if (![left, top, width, height].every((value) => isFinite(value))) {
      return undefined;
    }

    return { x: left + width / 2, y: top + height / 2 };
  }
}
